import React, { useState, useEffect } from 'react';
import {
    Settings, Save, RotateCcw, Search, ChevronDown, ChevronRight, Check, AlertCircle,
    Building, Eye, Cog, Printer, Database, Mail, Cloud, Truck, FileText, Package, HelpCircle, Archive
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { settingsService } from '../../../../services/settingsService';
import { systemSetupService, SystemSetupDto } from '../../../../services/systemSetupService';
import { lookupService, StatusCodeDto, RuleGroupDto, SqlGroupDto } from '../../../../services/lookupService';
import { printingService } from '../../../../services/printingService';
import { Report } from '../../../../types/printing';
import {
    SettingWithValue,
    SettingCategory
} from '../../../../types/settings';
import { PrinterSelect } from '../../../common/PrinterSelect';
import { AdvancedSettingsPanel } from '../../../common/settings/AdvancedSettingsPanel';
import { parseDefaultReportIdType } from '../../../../utils/reportSettings';
import { setCachedMaxLineItemQuantity } from '../../../../hooks/useMaxLineItemQuantity';

// ===== SYSTEMSETUP FIELD CONFIGURATION =====

type FieldType = 'text' | 'number' | 'boolean' | 'select' | 'password' | 'decimal' | 'datetime' | 'printer';

interface FieldConfig {
    name: keyof SystemSetupDto;
    label: string;
    type: FieldType;
    description?: string;
    lookupKey?: 'statusCode' | 'ruleGroup' | 'sqlGroup';
    statusType?: number;
    readOnly?: boolean;
    sqlField?: string;
    deprecated?: boolean;
    deprecatedMessage?: string;
    /** True if this field is no longer referenced in code/SP — kept for legacy data only.
     *  Will be moved to the "Legacy / Ubrukte"-tab and styled as inactive. */
    unused?: boolean;
    /** Optional reason/note shown in tooltip when unused. */
    unusedReason?: string;
}

interface FieldGroup {
    id: string;
    name: string;
    icon: React.ElementType;
    fields: FieldConfig[];
}

interface TabConfig {
    id: string;
    name: string;
    icon: React.ElementType;
    groups: FieldGroup[];
}

// Tab and field configuration based on legacy SystemSetup.dfm
const SYSTEM_SETUP_TABS: TabConfig[] = [
    {
        id: 'general',
        name: 'Generelt',
        icon: Building,
        groups: [
            {
                id: 'company',
                name: 'Firmainformasjon',
                icon: Building,
                fields: [
                    { name: 'companyName', label: 'Firmanavn', type: 'text', description: 'Firmanavn (fra lisens).', sqlField: 'CompanyName' },
                    { name: 'orgNo', label: 'Org.nr.', type: 'text', description: 'Organisasjonsnummer (fra lisens).', sqlField: 'OrgNo' },
                    { name: 'companyAddress1', label: 'Adresse 1', type: 'text', description: 'Angi firma-adresse. Kan benyttes i rapporter.', sqlField: 'CompanyAddress1' },
                    { name: 'companyZipCode', label: 'Postnr', type: 'text', description: 'Angi firma-postnummer. Kan benyttes i rapporter.', sqlField: 'CompanyZipCode' },
                    { name: 'companyCity', label: 'Poststed', type: 'text', description: 'Angi firma-poststed. Kan benyttes i rapporter.', sqlField: 'CompanyCity' },
                    { name: 'companyCountry', label: 'Land', type: 'text', description: 'Angi land. Kan benyttes i rapporter.', sqlField: 'CompanyCountry' },
                ]
            },
            {
                id: 'communication',
                name: 'E-post og SMS',
                icon: Mail,
                fields: [
                    { name: 'smsSenderAddress', label: 'SMS avsendernavn', type: 'text', sqlField: 'SMSSenderAddress', unused: true },
                    { name: 'smtpServer', label: 'SMTP server', type: 'text', description: 'Angi mailserver (SMTP).', sqlField: 'SMTPServer', unused: true },
                    { name: 'smtpPort', label: 'SMTP port', type: 'number', description: 'Angi port (25).', sqlField: 'SMTPPort', unused: true },
                    { name: 'smtpUser', label: 'SMTP brukernavn', type: 'text', description: 'Angi brukernavn.', sqlField: 'SMTPUser', unused: true },
                    { name: 'smtpPwd', label: 'SMTP passord', type: 'password', description: 'Angi passord.', sqlField: 'SMTPPwd', unused: true },
                    { name: 'smtpSenderName', label: 'SMTP avsendernavn', type: 'text', description: 'Angi avsendernavn for mailsending.', sqlField: 'SMTPSenderName', unused: true },
                    { name: 'smtpSenderAddress', label: 'SMTP avsenderadresse', type: 'text', description: 'Angi mailadresse for mailsending.', sqlField: 'SMTPSenderAddress', unused: true },
                    { name: 'smtpUseSSL', label: 'SMTP, benytt SSL', type: 'boolean', sqlField: 'SMTPUseSSL', unused: true },
                    { name: 'smtpUseTLS', label: 'SMTP, benytt TLS', type: 'select', lookupKey: 'statusCode', statusType: 29, sqlField: 'SMTPUseTLS', unused: true },
                ]
            },
            {
                id: 'storage',
                name: 'Skylagring',
                icon: Cloud,
                fields: [
                    { name: 'cloudStorageType', label: 'Skylagringstjeneste', type: 'select', lookupKey: 'statusCode', statusType: 23, sqlField: 'CloudStorageType', unused: true },
                    { name: 'cloudStorageAppKey', label: 'App key', type: 'text', sqlField: 'CloudStorageAppKey', unused: true },
                    { name: 'cloudStorageAppSecret', label: 'App secret', type: 'password', sqlField: 'CloudStorageAppSecret', unused: true },
                ]
            },
            {
                id: 'logging',
                name: 'Logging',
                icon: FileText,
                fields: [
                    { name: 'systemLogLevel', label: 'Loggnivå (server)', type: 'select', lookupKey: 'statusCode', statusType: 37, sqlField: 'SystemLogLevel', description: 'Minste nivå som lagres i serverloggen for denne tenanten. Informasjon er standard. Debug gir detaljert sporing (bl.a. plukkdanning) og bør bare stå på under feilsøking. Virker umiddelbart ved lagring.' },
                    { name: 'appLoglimit', label: 'Logg-grense app', type: 'number', description: 'Angir antall loggoppføringer som beholdes i APP før overføring til server.', sqlField: 'AppLoglimit', unused: true },
                    { name: 'archiveNumOfDays', label: 'Antall dager før arkivering av data', type: 'number', sqlField: 'ArchiveNumOfDays', unused: true },
                ]
            }
        ]
    },
    {
        id: 'display',
        name: 'Visning',
        icon: Eye,
        groups: [
            {
                id: 'display-settings',
                name: 'Visningsinnstillinger',
                icon: Eye,
                fields: [
                    { name: 'distributionYesNo', label: 'Distribusjon aktiv', type: 'boolean', description: 'Angi om bilder, knapper osv som tilhører distribusjonsmodul skal være synlig i planlegger.', sqlField: 'DistributionYesNo', unused: true, unusedReason: 'SystemSetup-versjonen brukes ikke. Order-tabellen har egen DistributionYesNo som er aktiv.' },
                    { name: 'ordersTxtVisibleOnPList', label: 'Vis ordretekster i planlegger', type: 'boolean', description: 'Skal ordretekster vises i planlegger? Disse vil synes under menyen for "Plukklister under arbeid".', sqlField: 'OrdersTxtVisibleOnPList', unused: true },
                    { name: 'pictureFolder', label: 'Bildekatalog', type: 'text', description: 'Angi sti for bildekatalog.', sqlField: 'PictureFolder', unused: true },
                ]
            }
        ]
    },
    {
        id: 'functionality',
        name: 'Funksjonalitet',
        icon: Cog,
        groups: [
            {
                id: 'warehouse',
                name: 'Lagerstyring',
                icon: Database,
                fields: [
                    { name: 'warehouseMethod', label: 'Lagernivå', type: 'select', lookupKey: 'statusCode', statusType: 4, sqlField: 'WarehouseMethod', unused: true },
                    { name: 'maxLineItemQuantity', label: 'Maks antall per linje', type: 'number', description: 'Høyeste vareantall en operatør kan registrere på én linje (plukk, mottak, plassering, flytt, telling, korreksjon m.fl.). Høyere antall avvises både i appen og på serveren — hindrer f.eks. at en EAN skannes inn i antallsfeltet. Tom eller 0 = ingen sperre.', sqlField: 'MaxLineItemQuantity' },
                    { name: 'maxNumberOfLabels', label: 'Maks antall utskrifter', type: 'number', description: 'Maks antall utskrifter per gang.', sqlField: 'MaxNumberOfLabels', unused: true },
                ]
            },
            {
                id: 'rule-groups',
                name: 'Regelgrupper',
                icon: Settings,
                fields: [
                    { name: 'stdRuleGroupID', label: 'Std. regelgruppe for plukk', type: 'select', lookupKey: 'ruleGroup', description: 'Velg regelgruppe for plukk som benyttes når du trykker på knappen "Lag nye plukklister".', sqlField: 'StdRuleGroupID' },
                    { name: 'stdDistrRuleGroupID', label: 'Std. regelgruppe for distribusjon', type: 'select', lookupKey: 'ruleGroup', description: 'Velg regelgruppe for distribusjon.', sqlField: 'StdDistrRuleGroupID', unused: true },
                    { name: 'stdPutAwayRuleGroupID', label: 'Standard regelgruppe for utlegg', type: 'select', lookupKey: 'ruleGroup', description: 'Regelgruppe som leses ved avslutning av varemottak — utleggsordrer genereres automatisk basert på reglene i denne gruppen. Påkrevd for at automatisk vareplassering skal fungere.', sqlField: 'StdPutAwayRuleGroupID' },
                    { name: 'stdReplenishmentRuleGroupID', label: 'Standard regelgruppe for påfylling', type: 'select', lookupKey: 'ruleGroup', description: 'Regelgruppe som kjøres av «Opprett påfylling» (Lager → Verktøy) — finner plukklokasjoner under minimumsnivå og oppretter påfyllingsordrer. Standard 1002 hvis ikke satt.', sqlField: 'StdReplenishmentRuleGroupID' },
                    { name: 'stdPutAwayListModeRuleGroupID', label: 'Std. regelgruppe for utlegg i listemodus', type: 'select', lookupKey: 'ruleGroup', description: 'Benyttes for å genere utlegg når mottaksmodus="Listemottak".', sqlField: 'StdPutAwayListModeRuleGroupID', unused: true },
                    { name: 'replenishmentRuleGroupIDFromApp', label: 'Std. regelgruppe for påfylling forespørsel fra APP', type: 'select', lookupKey: 'ruleGroup', description: 'Angi regelgruppe for påfyllingsforespørsel fra APP.', sqlField: 'ReplenishmentRuleGroupIDFromApp', unused: true },
                ]
            },
            {
                id: 'putaway',
                name: 'Utlegg og påfylling',
                icon: Package,
                fields: [
                    { name: 'createPutAwayOnInCompleteItems', label: 'Generer utlegg hvis manglende vareinfo', type: 'boolean', description: 'Genererer utleggsordre selv når varen mangler dimensjoner eller vekt-info. Hvis nei, må manglende info fylles inn først.', sqlField: 'CreatePutAwayOnInCompleteItems' },
                    { name: 'putAwayMaxOrderLines', label: 'Maks antall linjer per utleggsordre', type: 'number', description: 'Maksimalt antall linjer per utleggsordre. Større ordrer splittes automatisk i flere ordrer.', sqlField: 'PutAwayMaxOrderLines' },
                    { name: 'splitSharedBinsYesNo', label: 'Tillat deling av utlegg til felleslokasjoner', type: 'boolean', description: 'Tillater at samme lokasjon brukes til flere varer ved utlegg.', sqlField: 'SplitSharedBinsYesNo' },
                    { name: 'replenishmentMaxOrderLines', label: 'Maks antall linjer per påfyllingsordre', type: 'number', description: 'Maksimalt antall linjer per påfyllingsordre. Større ordrer splittes automatisk.', sqlField: 'ReplenishmentMaxOrderLines' },
                ]
            },
            {
                id: 'picking',
                name: 'Plukking',
                icon: Package,
                fields: [
                    { name: 'picklistStrainFactor', label: 'Fordelingsfaktor, plukkliste (samplukk)', type: 'number', description: 'Brukes av systemet for å beregne belastningen per operatør.', sqlField: 'PicklistStrainFactor' },
                    { name: 'orderStrainFactor', label: 'Fordelingsfaktor, ordre (samplukk)', type: 'number', description: 'Brukes av systemet for å beregne belastningen per operatør.', sqlField: 'OrderStrainFactor' },
                    { name: 'pListShortageRule', label: 'Plukklistebehandling ved manko', type: 'select', lookupKey: 'statusCode', statusType: 30, sqlField: 'PListShortageRule', description: 'Styrer manko-håndtering ved plukk-generering. «Åpen»: manko vises som [X]-linje på samme plukkliste. «Manko på egen plukkliste» (kun AW-lager): helt udekkede ordrelinjer flyttes til en egen plukkliste. Verdien «Manko» (lås) er utgått og behandles som «Åpen».' },
                    { name: 'pListSumUpOnItem', label: 'Slå sammen like varer på plukkliste', type: 'boolean', sqlField: 'PListSumUpOnItem', description: 'Ordrelinjer med samme vare og lager slås sammen til én plukklinje ved generering. Plukket antall fordeles automatisk tilbake på de opprinnelige ordrelinjene (korrekt ferdigmelding i ERP). Varer med serie-/batchkrav og ikke-lagerførte varer slås aldri sammen.' },
                    { name: 'stcReservationOrderByQty', label: 'Sorter på antall ved reservasjon', type: 'boolean', description: 'Ved reservasjon, prioriter lokasjoner med størst antall først. Tømmer ut lokasjoner raskere.', sqlField: 'StcReservationOrderByQty' },
                    { name: 'stcReplenishmentOrderByQty', label: 'Sorter på antall ved påfylling', type: 'boolean', description: 'Ved påfylling, prioriter etter antall i kildelokasjonen.', sqlField: 'StcReplenishmentOrderByQty' },
                ]
            },
            {
                id: 'external-shipping',
                name: 'Eksternt fraktprogram',
                icon: Truck,
                fields: [
                    { name: 'getExternalShipmentID', label: 'Integrer eksternt fraktprogram', type: 'boolean', description: 'Skal BCL integreres med et eksternt fraktprogram, J/N?', sqlField: 'GetExternalShipmentID' },
                    { name: 'consignmentSoftwareID', label: 'Eksternt fraktprogram', type: 'select', lookupKey: 'statusCode', statusType: 7, description: 'Angi navn på eksternt fraktprogram fra nedtrekksmenyen.', sqlField: 'ConsignmentSoftwareID' },
                    { name: 'consignmentSWURL', label: 'URL', type: 'text', description: 'Endpoint-URL for valgt fraktprogram (Cargonizer/AxiaFrakt/NShift).', sqlField: 'ConsignmentSWURL' },
                    { name: 'consignmentSWExternalID1', label: 'EksternID 1', type: 'text', description: 'Cargonizer: API Key. AxiaFrakt: API Key (UUID). NShift: ClientId.', sqlField: 'ConsignmentSWExternalID1' },
                    { name: 'consignmentSWExternalID2', label: 'EksternID 2', type: 'text', description: 'Cargonizer: Sender ID. AxiaFrakt: ImportID. NShift: ClientSecret.', sqlField: 'ConsignmentSWExternalID2' },
                    { name: 'externalShipmentVolumeFactor', label: 'Volumfaktor', type: 'decimal', description: 'Angir faktor som volum skal multipliseres med før det sendes til eksternt fraktprogram.', sqlField: 'ExternalShipmentVolumeFactor' },
                    { name: 'externalShipmentWeightFactor', label: 'Vektfaktor', type: 'decimal', description: 'Angir faktor som vekt skal multipliseres med før det sendes til eksternt fraktprogram.', sqlField: 'ExternalShipmentWeightFactor' },
                    { name: 'externalShipmentDimensionFactor', label: 'H/B/L faktor', type: 'decimal', description: 'Angir faktor som høyde/bredde/lengde skal multipliseres med før det sendes til eksternt fraktprogram.', sqlField: 'ExternalShipmentDimensionFactor' },
                ]
            },
            {
                id: 'other',
                name: 'Annet',
                icon: Settings,
                fields: [
                    { name: 'keepPOrdersHistory', label: 'Varemottak, behold innkjøpshistorikk', type: 'boolean', description: 'Hindrer sletting av innkjøpsordrer — sletteforsøk logges i stedet. Brukt av legacy SQL-trigger bcl_beforeDeletePOrders.', sqlField: 'KeepPOrdersHistory' },
                    { name: 'updateStockOnPListToPacking', label: 'Oppdater beholdninger ved pakking', type: 'boolean', description: 'Oppdater beholdninger ved overføring til pakking.', sqlField: 'UpdateStockOnPListToPacking', deprecated: true, deprecatedMessage: 'Denne innstillingen er ikke lenger i bruk. Beholdninger oppdateres alltid automatisk ved overføring til pakking.' },
                ]
            },
            {
                id: 'packing',
                name: 'Pakking',
                icon: Package,
                fields: [
                    { name: 'updateStockOnPListToPackingBinCode', label: 'Standard pakkelokasjon', type: 'text', description: 'Angi lokasjon som er definert som pakkelokasjon.', sqlField: 'UpdateStockOnPListToPackingBinCode' },
                    { name: 'packingSQLGroupID', label: 'Forsendelse, gruppering', type: 'select', lookupKey: 'sqlGroup', description: 'SQL-gruppe som styrer hvordan forsendelser grupperes i pakkemodulen.', sqlField: 'PackingSQLGroupID' },
                    { name: 'packingSQLVisibleGroupID', label: 'Forsendelse, grupperingsvisning (hoved)', type: 'select', lookupKey: 'sqlGroup', description: 'SQL-gruppe for visning av hovedgruppe-etikett i pakkelisten.', sqlField: 'PackingSQLVisibleGroupID' },
                    { name: 'packingSQLVisibleDetailGroupID', label: 'Forsendelse, grupperingsvisning (detalj)', type: 'select', lookupKey: 'sqlGroup', description: 'SQL-gruppe for visning av detaljgruppe-etikett i pakkelisten.', sqlField: 'PackingSQLVisibleDetailGroupID' },
                ]
            }
        ]
    },
    {
        id: 'printing',
        name: 'Utskrift',
        icon: Printer,
        groups: [
            {
                id: 'erp',
                name: 'ERP',
                icon: Printer,
                fields: [
                    { name: 'packageSlipERPYesNo', label: 'Pakkseddel fra ERP', type: 'boolean', description: 'Master-flagg på system-nivå for om pakkseddel skal genereres via ERP (Compass/eSENTRAL) ved eksport. Brukes når "Bruk leveringsmåte" er av — da må både dette og Profile-flagget være på. Når "Bruk leveringsmåte" er på, overstyres dette av DeliveryMethod.PackageSlipERPYesNo per ordre.', sqlField: 'PackageSlipERPYesNo' },
                    { name: 'packageSlipPrinterName', label: 'Skriver pakkseddel', type: 'printer', description: 'Standard-skriver for pakkseddel. Brukes som siste fallback hvis verken PackZone eller operatørens Profile har satt egen skriver. Skrives til PrintInfoPackingSlips i Orders-eksportfilen.', sqlField: 'PackageSlipPrinterName' },
                    { name: 'packageSlipFormNo', label: 'Formularnr pakkseddel', type: 'number', description: 'Formularnummer for pakkseddel som sendes til ERP. Skrives som FormNo:{n} i PrintInfoPackingSlips. Sett til 0 for å utelate feltet.', sqlField: 'PackageSlipFormNo' },
                    { name: 'packageSlipNumOfCopies', label: 'Antall kopier pakkseddel', type: 'number', description: 'Antall kopier av pakkseddelen som ERP skal skrive ut. Brukes som fallback hvis PackZone og Profile ikke har egen verdi. Skrives som Qty:{n} i PrintInfoPackingSlips.', sqlField: 'PackageSlipNumOfCopies' },
                    { name: 'shippingLabelsERPYesNo', label: 'Etiketter fra ERP', type: 'boolean', description: 'Master-flagg på system-nivå for om transport-/fraktetiketter skal genereres via ERP. Brukes når "Bruk leveringsmåte" er av — da må både dette og Profile-flagget være på. Når "Bruk leveringsmåte" er på, overstyres dette av DeliveryMethod.ShippingLabelsERPYesNo per ordre.', sqlField: 'ShippingLabelsERPYesNo' },
                    { name: 'shippingLabelsPrinterName', label: 'Skriver etikett', type: 'printer', description: 'Standard-skriver for transportetiketter. Brukes som siste fallback hvis verken PackZone eller operatørens Profile har satt egen skriver. Skrives til PrintInfoLabels i Orders-eksportfilen.', sqlField: 'ShippingLabelsPrinterName' },
                    { name: 'shippingLabelsFormNo', label: 'Formularnr etikett', type: 'number', description: 'Formularnummer for transportetiketter som sendes til ERP. Skrives som FormNo:{n} i PrintInfoLabels. Sett til 0 for å utelate feltet.', sqlField: 'ShippingLabelsFormNo' },
                    { name: 'consignmentNoteERPYesNo', label: 'Fraktbrev fra ERP', type: 'boolean', description: 'Master-flagg på system-nivå for om fraktbrev skal genereres via ERP. Brukes når "Bruk leveringsmåte" er av — da må både dette og Profile-flagget være på. Når "Bruk leveringsmåte" er på, overstyres dette av DeliveryMethod.ConsignmentNoteERPYesNo per ordre.', sqlField: 'ConsignmentNoteERPYesNo' },
                    { name: 'consignmentNotePrinterName', label: 'Skriver fraktbrev', type: 'printer', description: 'Standard-skriver for fraktbrev. Brukes som siste fallback hvis verken PackZone eller operatørens Profile har satt egen skriver. Skrives til PrintInfoConsigntmentNotes i Orders-eksportfilen.', sqlField: 'ConsignmentNotePrinterName' },
                    { name: 'consignmentNoteFormNo', label: 'Formularnr fraktbrev', type: 'number', description: 'Formularnummer for fraktbrev som sendes til ERP. Skrives som FormNo:{n} i PrintInfoConsigntmentNotes. Sett til 0 for å utelate feltet.', sqlField: 'ConsignmentNoteFormNo' },
                ]
            },
            {
                id: 'bcl',
                name: 'Compass',
                icon: Printer,
                fields: [
                    { name: 'reportFolder', label: 'Rapportkatalog', type: 'text', description: 'Angi katalog for rapporter som skal benyttes av BCL.', sqlField: 'ReportFolder' },
                    { name: 'printSetupFromDMYesNo', label: 'Bruk leveringsmåte', type: 'boolean', description: 'Master-bryter for hvor utskriftsflaggene (PackageSlip/Labels/ConsignmentNote ERPYesNo) leses fra ved bygging av PrintInfo-felter i Orders-eksportfilen. PÅ: leveringsmåten (DeliveryMethod) på hver ordre bestemmer hvilke dokumenter ERP skal generere — fleksibelt per ordre. AV: SystemSetup + Profile-flaggene (AND-logikk) bestemmer det samme for alle ordrer. Påvirker ikke selve skriverhierarkiet (PackZone → Profile → SystemSetup).', sqlField: 'PrintSetupFromDMYesNo' },
                ]
            },
            {
                id: 'external-shipping-print',
                name: 'Eksternt fraktprogram',
                icon: Truck,
                fields: [
                    { name: 'shippingLabelsYesNo', label: 'Transportetiketter', type: 'boolean', description: 'Aktiver utskrift av transportetiketter via integrert fraktprogram.', sqlField: 'ShippingLabelsYesNo' },
                    { name: 'shippingLabelsLayoutNo', label: 'Etikettlayout (transport)', type: 'number', description: 'Velg hvilket filformat som skal eksporteres til eksternt fraktprogram.', sqlField: 'ShippingLabelsLayoutNo', unused: true, unusedReason: 'SystemSetup-versjonen brukes ikke. DeliveryMethod har eget felt som er aktivt.' },
                    { name: 'printerID', label: 'PrinterID fileksport (transport)', type: 'text', description: 'Navn eller ID som identifiserer printer i eksternt fraktprogram.', sqlField: 'PrinterID', unused: true },
                    { name: 'packagePrinterID', label: 'PrinterID fileksport (emballasje)', type: 'text', description: 'Angi printerID for eksternt fraktprogram.', sqlField: 'PackagePrinterID', unused: true, unusedReason: 'Profile.PackagePrinterID brukes i stedet (per operatør).' },
                    { name: 'packageLabelsLayoutNo', label: 'Etikettlayout (emballasje)', type: 'number', description: 'Velg hvilket filformat som skal eksporteres til eksternt utskriftsprogram.', sqlField: 'PackageLabelsLayoutNo', unused: true },
                ]
            },
            {
                id: 'export-folders',
                name: 'Eksportkatalog',
                icon: FileText,
                fields: [
                    { name: 'labelExportFolder', label: 'Eksportkatalog merkelapper (transport)', type: 'text', description: 'Angi eksportkatalog for merkelapp-filer (transport) - Eksternt fraktprogram.', sqlField: 'LabelExportFolder' },
                    { name: 'packageLabelExportFolder', label: 'Eksportkatalog merkelapper (emballasje)', type: 'text', description: 'Angi eksportkatalog for merkelapp-filer (emballasje).', sqlField: 'PackageLabelExportFolder' },
                ]
            }
        ]
    },
    {
        id: 'export',
        name: 'Eksport av data',
        icon: Database,
        groups: [
            {
                id: 'export-folders-main',
                name: 'Eksportkataloger',
                icon: FileText,
                fields: [
                    { name: 'orderExportFolder', label: 'Eksportkatalog ordrefiler', type: 'text', description: 'Angi eksportkatalogen hvor ordrefiler skal lagres. Benyttes for videre oppdatering i ERP-system.', sqlField: 'OrderExportFolder' },
                    { name: 'grExportFolder', label: 'Eksportkatalog varemottak', type: 'text', description: 'Angi eksportkatalogen hvor varemottakfiler skal lagres.', sqlField: 'GRExportFolder' },
                    { name: 'stockTransExportFolder', label: 'Eksportkatalog lagertransaksjoner', type: 'text', description: 'Angi eksportkatalogen hvor lagertransaksjonsfiler skal lagres.', sqlField: 'StockTransExportFolder' },
                ]
            },
            {
                id: 'export-options',
                name: 'Eksportalternativer',
                icon: Database,
                fields: [
                    { name: 'useExternalEvents', label: 'Generer eksterne hendelser (SQL)', type: 'boolean', description: 'Genererer eksterne SQL-hendelser ved status-endringer for integrasjon med tredjepartssystemer.', sqlField: 'UseExternalEvents' },
                    { name: 'exportShipmentFiles', label: 'Eksporter pakkseddelgrunnlag', type: 'boolean', description: 'Eksporterer pakkseddelgrunnlag til fil ved fullført forsendelse.', sqlField: 'ExportShipmentFiles' },
                    { name: 'exportShipmentLineTo', label: 'Eksporter kolliinformasjon til', type: 'number', description: 'Filformat-ID for hvor kolliinformasjon skal eksporteres.', sqlField: 'ExportShipmentLineTo' },
                ]
            },
            {
                id: 'external-reporting',
                name: 'Ekstern rapportering',
                icon: FileText,
                fields: [
                    { name: 'exportToExternalReporting', label: 'Eksporter data til ekstern rapportering', type: 'boolean', sqlField: 'ExportToExternalReporting', unused: true },
                    { name: 'extReportingPathComplete', label: 'Katalog, full eksport', type: 'text', sqlField: 'ExtReportingPathComplete', unused: true },
                    { name: 'extReportingPathChanges', label: 'Katalog, endringer', type: 'text', sqlField: 'ExtReportingPathChanges', unused: true },
                    { name: 'extReportingLastChangesExport', label: 'Siste eksport av endringer', type: 'datetime', readOnly: true, sqlField: 'ExtReportingLastChangesExport', unused: true },
                    { name: 'extReportingCompleteExportTime', label: 'Tidspunkt for full eksport', type: 'datetime', readOnly: true, sqlField: 'ExtReportingCompleteExportTime', unused: true },
                    { name: 'extReportingLink', label: 'Link til ekstern rapportering', type: 'text', sqlField: 'ExtReportingLink', unused: true },
                ]
            }
        ]
    }
];

// ===== LEGACY/UNUSED TAB =====
// Auto-generated tab that collects all fields marked with `unused: true`,
// grouped by their original tab and group. Lets administrators still
// view/edit these fields if needed, but keeps them out of the main UI.
const LEGACY_TAB: TabConfig = {
    id: 'legacy',
    name: 'Legacy / ubrukte',
    icon: Archive,
    groups: SYSTEM_SETUP_TABS.flatMap(tab =>
        tab.groups
            .map(group => ({
                id: `legacy-${tab.id}-${group.id}`,
                name: `${tab.name} → ${group.name}`,
                icon: group.icon,
                fields: group.fields.filter(f => f.unused)
            }))
            .filter(g => g.fields.length > 0)
    )
};

const ALL_TABS: TabConfig[] = [...SYSTEM_SETUP_TABS, LEGACY_TAB];

// ===== LOOKUP DATA TYPES =====
interface LookupData {
    statusCodes: Record<number, StatusCodeDto[]>;
    ruleGroups: RuleGroupDto[];
    sqlGroups: SqlGroupDto[];
}

// ===== MAIN COMPONENT =====
const SystemSettings: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    // Main tab state: 'classic' (SystemSetup) or 'advanced' (SettingDefinition)
    const [mainTab, setMainTab] = useState<'classic' | 'advanced'>('classic');

    // Classic (SystemSetup) state
    const [systemSetup, setSystemSetup] = useState<SystemSetupDto | null>(null);
    const [editedSetup, setEditedSetup] = useState<Partial<SystemSetupDto>>({});
    const [activeTab, setActiveTab] = useState('general');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['company']));
    const [lookupData, setLookupData] = useState<LookupData>({
        statusCodes: {},
        ruleGroups: [],
        sqlGroups: []
    });
    const [lookupLoading, setLookupLoading] = useState(true);

    // Aktive rapportmaler per rapporttype for «DefaultReportId:{type}»-nedtrekkene.
    // Typene utledes av settingnøklene ved lasting (DefaultReportId:1, :40, :50 osv.).
    const [reportsByType, setReportsByType] = useState<Record<number, Report[]>>({});

    // Advanced (SettingDefinition) state
    const [settings, setSettings] = useState<SettingWithValue[]>([]);
    const [categories, setCategories] = useState<SettingCategory[]>([]);

    // Shared state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [showSqlFields, setShowSqlFields] = useState(false);

    useEffect(() => {
        loadAllData();
    }, [user?.companyNo]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const loadAllData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [, , advancedSettings] = await Promise.all([
                loadSystemSetup(),
                loadLookupData(),
                loadAdvancedSettings()
            ]);
            await loadReportDropdowns(advancedSettings);
        } catch (err) {
            console.error('Failed to load data:', err);
            setError(t('manager.modules.adminHost.systemSettings.loadDataError', {}, 'Kunne ikke laste data'));
        } finally {
            setLoading(false);
        }
    };

    const loadSystemSetup = async () => {
        const data = await systemSetupService.getCurrent();
        setSystemSetup(data);
        setEditedSetup(data);
        setHasChanges(false);
    };

    /**
     * Laster aktive rapportmaler for hver rapporttype som har en «DefaultReportId:{type}»-
     * setting, slik at verdien kan velges fra nedtrekk i stedet for å tastes som rå ID.
     */
    const loadReportDropdowns = async (settingsList: SettingWithValue[]) => {
        if (!user?.companyNo) return;
        const types = Array.from(new Set(
            settingsList
                .map(s => parseDefaultReportIdType(s.definition.settingKey))
                .filter((t): t is number => t !== null)
        ));
        if (types.length === 0) return;
        try {
            const results = await Promise.all(
                types.map(async (type) => {
                    try {
                        return [type, await printingService.getReports(user.companyNo, type, true)] as const;
                    } catch {
                        return [type, [] as Report[]] as const;
                    }
                })
            );
            setReportsByType(Object.fromEntries(results));
        } catch (err) {
            console.warn('Could not load report templates for DefaultReportId dropdowns:', err);
            setReportsByType({});
        }
    };

    const loadLookupData = async () => {
        try {
            setLookupLoading(true);
            const statusTypes = [4, 7, 23, 29, 30, 37];
            const statusCodePromises = statusTypes.map(type =>
                lookupService.getStatusCodes(type).catch(() => [] as StatusCodeDto[])
            );

            const [
                ruleGroups,
                sqlGroups,
                ...statusCodeResults
            ] = await Promise.all([
                lookupService.getRuleGroups().catch(() => [] as RuleGroupDto[]),
                lookupService.getSqlGroups().catch(() => [] as SqlGroupDto[]),
                ...statusCodePromises
            ]);

            const statusCodes: Record<number, StatusCodeDto[]> = {};
            statusTypes.forEach((type, index) => {
                statusCodes[type] = statusCodeResults[index] || [];
            });

            setLookupData({ statusCodes, ruleGroups, sqlGroups });
        } finally {
            setLookupLoading(false);
        }
    };

    const loadAdvancedSettings = async (): Promise<SettingWithValue[]> => {
        const data = await settingsService.getSystemSettings();
        setSettings(data);
        // Kategori-visningsnavn fra katalogen i master (rå nøkkel som fallback ved feil)
        try {
            setCategories(await settingsService.getCategories());
        } catch {
            setCategories([]);
        }
        return data;
    };

    // ===== CLASSIC (SYSTEMSETUP) HANDLERS =====

    const handleFieldChange = (fieldName: keyof SystemSetupDto, value: any) => {
        setEditedSetup(prev => ({ ...prev, [fieldName]: value }));
        setHasChanges(true);
    };

    const saveSystemSetup = async () => {
        try {
            setSaving('systemSetup');
            setError(null);
            const saved = await systemSetupService.updateCurrent(editedSetup);
            setCachedMaxLineItemQuantity(saved.maxLineItemQuantity);
            await loadSystemSetup();
            setSuccessMessage(t('manager.modules.adminHost.systemSettings.savedSuccess', {}, 'Systeminnstillinger lagret'));
        } catch (err: any) {
            console.error('Failed to save system setup:', err);
            setError(err.message || t('manager.modules.adminHost.systemSettings.saveError', {}, 'Kunne ikke lagre systeminnstillinger'));
        } finally {
            setSaving(null);
        }
    };

    const resetSystemSetup = () => {
        if (systemSetup) {
            setEditedSetup(systemSetup);
            setHasChanges(false);
        }
    };

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const getFieldValue = (fieldName: keyof SystemSetupDto): any => {
        return editedSetup[fieldName] ?? '';
    };

    const getSelectOptions = (field: FieldConfig): { value: string | number; label: string }[] => {
        if (field.lookupKey === 'statusCode' && field.statusType) {
            const codes = lookupData.statusCodes[field.statusType] || [];
            return codes.map(c => ({ value: c.statusCode, label: c.lng_1 }));
        }
        if (field.lookupKey === 'ruleGroup') {
            return lookupData.ruleGroups.map(g => ({ value: g.ruleGroupID, label: g.lng1 }));
        }
        if (field.lookupKey === 'sqlGroup') {
            return lookupData.sqlGroups.map(g => ({ value: g.sqlGroupID, label: g.lng1 }));
        }
        return [];
    };

    const renderFieldInput = (field: FieldConfig) => {
        const value = getFieldValue(field.name);
        const isDisabled = field.readOnly || field.deprecated || saving === 'systemSetup';
        const baseClass = "w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100";

        switch (field.type) {
            case 'boolean':
                return (
                    <select
                        value={value === true || value === 'true' ? 'true' : value === false || value === 'false' ? 'false' : ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
                        disabled={isDisabled}
                        className={baseClass}
                    >
                        <option value="">{t('manager.modules.adminHost.systemSettings.selectPlaceholder', {}, '-- Velg --')}</option>
                        <option value="true">{t('manager.modules.adminHost.systemSettings.yes', {}, 'Ja')}</option>
                        <option value="false">{t('manager.modules.adminHost.systemSettings.no', {}, 'Nei')}</option>
                    </select>
                );
            case 'select':
                const options = getSelectOptions(field);
                return (
                    <select
                        value={value ?? ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value ? Number(e.target.value) : null)}
                        disabled={isDisabled || lookupLoading}
                        className={baseClass}
                    >
                        <option value="">{t('manager.modules.adminHost.systemSettings.selectPlaceholder', {}, '-- Velg --')}</option>
                        {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                );
            case 'number':
                return (
                    <input
                        type="number"
                        value={value ?? ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value ? Number(e.target.value) : null)}
                        disabled={isDisabled}
                        className={baseClass}
                    />
                );
            case 'decimal':
                return (
                    <input
                        type="number"
                        step="0.01"
                        value={value ?? ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value ? parseFloat(e.target.value) : null)}
                        disabled={isDisabled}
                        className={baseClass}
                    />
                );
            case 'password':
                return (
                    <input
                        type="password"
                        value={value ?? ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value || null)}
                        disabled={isDisabled}
                        className={baseClass}
                    />
                );
            case 'datetime':
                return (
                    <input
                        type="text"
                        value={value ? new Date(value).toLocaleString('nb-NO') : ''}
                        disabled={true}
                        className={baseClass + " bg-gray-100"}
                    />
                );
            case 'printer':
                return (
                    <PrinterSelect
                      scope="tenant"
                        value={value}
                        onChange={(v) => handleFieldChange(field.name, v)}
                        disabled={isDisabled}
                        showTestButton={true}
                        size="sm"
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        value={value ?? ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value || null)}
                        disabled={isDisabled}
                        className={baseClass}
                    />
                );
        }
    };

    const renderFieldRow = (field: FieldConfig) => {
        const isInactive = field.deprecated || field.unused;
        const unusedTooltip = field.unusedReason
            || t('manager.modules.adminHost.systemSettings.unusedTooltipDefault', {}, 'Denne innstillingen er ikke lenger referert i koden, stored procedures eller forretningslogikk. Beholdt for legacy-data.');
        return (
            <div key={field.name} className={`grid grid-cols-12 gap-2 py-1.5 border-b border-gray-100 last:border-b-0 items-center ${isInactive ? 'opacity-60' : ''}`}>
                <div className="col-span-5">
                    <div className="flex items-center gap-1 leading-tight">
                        <span className={`text-sm ${isInactive ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{field.label}</span>
                        {field.deprecated && (
                            <span className="inline-flex items-center self-center px-1.5 py-px rounded text-[10px] font-medium leading-none bg-amber-100 text-amber-700 border border-amber-200">
                                {t('manager.modules.adminHost.systemSettings.inactive', {}, 'Inaktiv')}
                            </span>
                        )}
                        {field.unused && !field.deprecated && (
                            <span className="inline-flex items-center self-center px-1.5 py-px rounded text-[10px] font-medium leading-none bg-gray-100 text-gray-600 border border-gray-300" title={unusedTooltip}>
                                {t('manager.modules.adminHost.systemSettings.unused', {}, 'Ubrukt')}
                            </span>
                        )}
                        {field.description && !isInactive && (
                            <div className="relative group">
                                <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                                <div className="absolute left-0 top-full mt-1 hidden group-hover:block w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[60]">
                                    <div className="absolute left-2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                    {field.description}
                                </div>
                            </div>
                        )}
                        {field.deprecatedMessage && (
                            <div className="relative group">
                                <HelpCircle className="w-3.5 h-3.5 text-amber-400 cursor-help" />
                                <div className="absolute left-0 top-full mt-1 hidden group-hover:block w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[60]">
                                    <div className="absolute left-2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                    {field.deprecatedMessage}
                                </div>
                            </div>
                        )}
                        {field.unused && !field.deprecated && (
                            <div className="relative group">
                                <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                                <div className="absolute left-0 top-full mt-1 hidden group-hover:block w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[60]">
                                    <div className="absolute left-2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                    {unusedTooltip}
                                </div>
                            </div>
                        )}
                    </div>
                    {showSqlFields && field.sqlField && (
                        <span className="block text-[10px] text-gray-400 font-mono">[{field.sqlField}]</span>
                    )}
                </div>
                <div className="col-span-7">
                    {renderFieldInput(field)}
                </div>
            </div>
        );
    };

    // Filter fields based on search term
    const filterFields = (fields: FieldConfig[]): FieldConfig[] => {
        if (!searchTerm.trim()) return fields;
        const lowerSearch = searchTerm.toLowerCase();
        return fields.filter(f =>
            f.label.toLowerCase().includes(lowerSearch) ||
            f.description?.toLowerCase().includes(lowerSearch) ||
            f.name.toLowerCase().includes(lowerSearch) ||
            f.sqlField?.toLowerCase().includes(lowerSearch)
        );
    };

    const renderClassicTab = () => {
        const currentTabConfig = ALL_TABS.find(t => t.id === activeTab);
        if (!currentTabConfig) return null;

        const isLegacyTab = currentTabConfig.id === 'legacy';

        // Hide unused fields from regular tabs (they appear under "Legacy / ubrukte")
        const filteredGroups = currentTabConfig.groups
            .map(group => ({
                ...group,
                fields: filterFields(isLegacyTab ? group.fields : group.fields.filter(f => !f.unused))
            }))
            .filter(group => group.fields.length > 0);

        const totalUnused = LEGACY_TAB.groups.reduce((sum, g) => sum + g.fields.length, 0);

        return (
            <div className="space-y-4">
                {/* Sub-tabs */}
                <div className="flex border-b border-gray-200 overflow-x-auto">
                    {ALL_TABS.map(tab => {
                        const Icon = tab.icon;
                        const isLegacy = tab.id === 'legacy';
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : isLegacy
                                            ? 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300 italic'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                                title={isLegacy ? t('manager.modules.adminHost.systemSettings.legacyTabTooltip', {}, 'Innstillinger som ikke lenger benyttes i koden — beholdt for legacy-data') : undefined}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.name}
                                {isLegacy && totalUnused > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-semibold">
                                        {totalUnused}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {isLegacyTab && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <strong>{t('manager.modules.adminHost.systemSettings.legacyBannerTitle', {}, 'Legacy / ubrukte innstillinger.')}</strong> {t('manager.modules.adminHost.systemSettings.legacyBannerBody', {}, 'Feltene under er ikke lenger referert i koden, stored procedures eller forretningslogikk i .NET-løsningen. De er beholdt synlig for å støtte eldre data og eventuelle integrasjoner. Endringer her vil sannsynligvis ikke ha effekt.')}
                        </div>
                    </div>
                )}

                {/* Field groups */}
                {filteredGroups.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        {searchTerm ? t('manager.modules.adminHost.systemSettings.noFieldsForSearch', {}, 'Ingen felt funnet for søket') : t('manager.modules.adminHost.systemSettings.noFieldsInCategory', {}, 'Ingen felt i denne kategorien')}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredGroups.map(group => {
                            const Icon = group.icon;
                            const isExpanded = expandedGroups.has(group.id);
                            return (
                                // Ikke overflow-hidden: den klippet hjelpetekst-tooltipen på nederste innstilling i kategorien.
                                // Hjørnene rundes i stedet på header/innhold.
                                <div key={group.id} className="border border-gray-200 rounded-lg">
                                    <button
                                        onClick={() => toggleGroup(group.id)}
                                        className={`w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg ${isExpanded ? '' : 'rounded-b-lg'}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {isExpanded ? (
                                                <ChevronDown className="w-4 h-4 text-gray-500" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-gray-500" />
                                            )}
                                            <Icon className="w-4 h-4 text-gray-600" />
                                            <span className="font-medium text-gray-800 text-sm">{group.name}</span>
                                            <span className="text-xs text-gray-500">({group.fields.length})</span>
                                        </div>
                                    </button>
                                    {isExpanded && (
                                        <div className="px-4 py-2 bg-white rounded-b-lg">
                                            {group.fields.map(field => renderFieldRow(field))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Save/Reset buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                        onClick={resetSystemSetup}
                        disabled={!hasChanges || saving === 'systemSetup'}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <RotateCcw className="w-4 h-4" />
                        {t('manager.modules.adminHost.systemSettings.reset', {}, 'Tilbakestill')}
                    </button>
                    <button
                        onClick={saveSystemSetup}
                        disabled={!hasChanges || saving === 'systemSetup'}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving === 'systemSetup' ? (
                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {t('manager.modules.adminHost.systemSettings.saveChanges', {}, 'Lagre endringer')}
                    </button>
                </div>
            </div>
        );
    };

    // ===== ADVANCED (SETTINGDEFINITION) HANDLERS =====

    const saveAdvancedValue = async (settingKey: string, value: string): Promise<boolean> => {
        try {
            setSaving(settingKey);
            setError(null);
            await settingsService.setSystemSetting(settingKey, value || undefined);
            await loadAdvancedSettings();
            setSuccessMessage(t('manager.modules.adminHost.systemSettings.settingSaved', {}, 'Innstilling lagret'));
            return true;
        } catch (err: any) {
            console.error('Failed to save setting:', err);
            setError(err.message || t('manager.modules.adminHost.systemSettings.settingSaveError', {}, 'Kunne ikke lagre innstilling'));
            return false;
        } finally {
            setSaving(null);
        }
    };

    const resetAdvancedToDefault = async (settingKey: string) => {
        try {
            setSaving(settingKey);
            setError(null);
            await settingsService.resetSystemSetting(settingKey);
            await loadAdvancedSettings();
            setSuccessMessage(t('manager.modules.adminHost.systemSettings.settingReset', {}, 'Innstilling tilbakestilt til standard'));
        } catch (err: any) {
            console.error('Failed to reset setting:', err);
            setError(err.message || t('manager.modules.adminHost.systemSettings.settingResetError', {}, 'Kunne ikke tilbakestille innstilling'));
        } finally {
            setSaving(null);
        }
    };

    const renderAdvancedTab = () => (
        <AdvancedSettingsPanel
            mode="system"
            settings={settings}
            categories={categories}
            reportsByType={reportsByType}
            savingKey={saving}
            searchTerm={searchTerm}
            onSave={saveAdvancedValue}
            onReset={resetAdvancedToDefault}
        />
    );

    // ===== RENDER =====

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">{t('manager.modules.adminHost.systemSettings.loadingSettings', {}, 'Laster innstillinger...')}</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                    <Settings className="w-8 h-8" />
                    <h1 className="text-2xl font-bold">{t('manager.modules.adminHost.systemSettings.title', {}, 'Systeminnstillinger')}</h1>
                </div>
                <p className="text-slate-200">{t('manager.modules.adminHost.systemSettings.subtitle', {}, 'Konfigurer globale innstillinger for lagersystemet')}</p>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <Check className="w-5 h-5" /> {successMessage}
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" /> {error}
                </div>
            )}

            {/* Main tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setMainTab('classic')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            mainTab === 'classic'
                                ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Cog className="w-4 h-4" />
                            {t('manager.modules.adminHost.systemSettings.tabClassic', {}, 'Systemkonfigurasjon')}
                        </div>
                    </button>
                    <button
                        onClick={() => setMainTab('advanced')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            mainTab === 'advanced'
                                ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Database className="w-4 h-4" />
                            {t('manager.modules.adminHost.systemSettings.tabAdvanced', {}, 'Avanserte innstillinger')}
                        </div>
                    </button>
                </div>

                {/* Search and toolbar */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('manager.modules.adminHost.systemSettings.searchPlaceholder', {}, 'Søk i innstillinger...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        {/* SQL field toggle - only show for classic tab */}
                        {mainTab === 'classic' && (
                            <button
                                onClick={() => setShowSqlFields(!showSqlFields)}
                                className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg transition-colors whitespace-nowrap ${
                                    showSqlFields
                                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                        : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                                }`}
                                title={t('manager.modules.adminHost.systemSettings.toggleSqlFields', {}, 'Vis/skjul SQL-feltnavn')}
                            >
                                <Database className="w-4 h-4" />
                                SQL
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-4">
                    {mainTab === 'classic' ? renderClassicTab() : renderAdvancedTab()}
                </div>
            </div>
        </div>
    );
};

export default SystemSettings;
